import { z } from 'zod';
/**
 * Emoji payload bound. A unicode emoji cluster with skin-tone and
 * zero-width-joiner sequences tops out around 20 UTF-16 units (e.g.
 * family emoji); 32 gives us comfortable headroom without letting an
 * attacker paste a DoS-sized string into `AuditAction.REACTION_ADD`
 * or the Reaction table's `emoji` column.
 */
export const MAX_EMOJI_LENGTH = 32;
// eslint-disable-next-line no-control-regex -- intentional: reject control bytes
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;
/**
 * One emoji grapheme cluster, expressed with Unicode property escapes:
 *
 *  - a regional-indicator pair (flags: 🇦🇺)
 *  - a keycap sequence (1️⃣, #️⃣)
 *  - a pictograph with optional presentation selector (VS15/VS16), optional
 *    skin-tone modifier, optional tag sequence (subdivision flags), joined to
 *    further pictographs with ZWJ (👨‍👩‍👧, ❤️‍🔥, 🧑🏽‍🤝‍🧑🏻)
 *
 * `Extended_Pictographic` covers the text-presentation symbols the clients
 * use as quick reactions (✅ ❌ ❤️ ☑️) but excludes bare digits and letters, so
 * "1", "ok" and "ok👍" are all rejected.
 *
 * Kept as a string so the RegExp can be constructed lazily inside try/catch:
 * JS engines without Unicode property-escape support (older Hermes builds
 * on mobile) throw a SyntaxError at construction time, and a thrown error at
 * module load would take the whole shared package down with it.
 */
const PICTOGRAPH = '\\p{Extended_Pictographic}[\\uFE0E\\uFE0F]?\\p{Emoji_Modifier}?';
const TAG_SEQUENCE = '(?:[\\u{E0020}-\\u{E007E}]+\\u{E007F})?';
const EMOJI_CLUSTER = '(?:\\p{Regional_Indicator}{2}' +
    '|[0-9#*]\\uFE0F?\\u20E3' +
    `|${PICTOGRAPH}${TAG_SEQUENCE}(?:\\u200D${PICTOGRAPH}${TAG_SEQUENCE})*)`;
const EMOJI_SEQUENCE_SOURCE = `^${EMOJI_CLUSTER}+$`;
function compileEmojiSequencePattern() {
    try {
        return new RegExp(EMOJI_SEQUENCE_SOURCE, 'u');
    }
    catch {
        return null;
    }
}
const EMOJI_SEQUENCE_RE = compileEmojiSequencePattern();
/**
 * True when `value` is one or more emoji grapheme clusters and nothing else.
 * The caller is expected to have trimmed surrounding whitespace already.
 *
 * Falls back to the length-and-control-character check alone when the host
 * engine cannot build a Unicode-property RegExp, so a client on such an
 * engine can still send reactions and the server (Node) remains the
 * authoritative gate.
 */
export function isEmojiSequence(value) {
    if (typeof value !== 'string')
        return false;
    if (value.length === 0 || value.length > MAX_EMOJI_LENGTH)
        return false;
    if (CONTROL_CHARS.test(value))
        return false;
    if (!EMOJI_SEQUENCE_RE)
        return true;
    return EMOJI_SEQUENCE_RE.test(value);
}
export const addReactionSchema = z.object({
    emoji: z
        .string()
        .trim()
        .min(1, 'Emoji cannot be empty')
        .max(MAX_EMOJI_LENGTH, `Emoji cannot exceed ${MAX_EMOJI_LENGTH} characters`)
        .refine(isEmojiSequence, 'Reaction must be an emoji'),
});
//# sourceMappingURL=reaction.schema.js.map